import React, { useCallback, useState } from 'react';

import styled, { css } from 'styled-components';

import ScrubsThread from '~/icons/fabletics/ScrubsThread';
import ItemEmbroideryDetails from '~/shared/components/ItemEmbroideryDetails';
import Text from '~/shared/components/Text';
import { Button, Separator } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import EmbroideryModal from '../EmbroideryModal';
import { useProductDetailEmbroideryContext } from '../ProductDetailCTAs/ProductDetailEmbroideryContext';

const Wrapper = styled.div`
  display: flex;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius};
  padding: 16px;
  gap: 16px;
  box-shadow: 0px 2px 1px rgba(0, 0, 0, 0.08);
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  flex: 2;
`;

const EmbroideryButton = styled(Button)`
  border-width: 1.5px;
  margin: 8px 0;
`;

const labelStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: bold;
  letter-spacing: ${-0.4 / 18}em;
`;

const EmbroideryLabel = styled(Text).attrs({ variant: 'sectionText' })`
  text-align: left;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.textEmphasized};
`;

const EmbroideryDetailsWrapper = styled.span`
  & > div {
    margin: 0;
  }
`;

const NoReturnsLabel = styled.div`
  font-size: 12px;
`;

const detailLineItemLabelStyles = css`
  text-align: left;
  color: ${({ theme }) => theme.colors.textEmphasized};
  ${({ theme }) => theme.typography.variants.sectionText.textStyle};
`;

const detailLineItemValueStyles = css`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textEmphasized};
`;

const customizationTextStyles = css`
  font-weight: 400;
`;

const ClearButton = styled(Button)`
  align-self: flex-start;
  color: ${({ theme }) => theme.colors.textFadedAlternate};
  border-bottom: 1px solid ${({ theme }) => theme.colors.textFadedAlternate};

  ${({ theme }) => theme.typography.variants.bodyMed.textStyle};
`;

const separatorStyles = 'margin: 8px 0';

export default function ProductDetailEmbroiderySelector() {
  const {
    addEmbroidery,
    iconPersonalizations,
    textPersonalizations,
    handleEmbroideryValidation,
    productEmbroidery,
  } = useProductDetailEmbroideryContext();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleModalOpen = useCallback((event) => {
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const onCTAClick = useCallback(
    (embroideryContext) => {
      try {
        setIsLoading(true);
        handleEmbroideryValidation();
        addEmbroidery(embroideryContext);
        handleModalClose();
      } catch (error) {
        // TODO: Custom error handling here
      }
      setIsLoading(false);
    },
    [addEmbroidery, handleEmbroideryValidation, handleModalClose]
  );

  const handleClear = () => {
    addEmbroidery([]);
  };

  const hasEmbroidery =
    Array.isArray(productEmbroidery) && productEmbroidery.length > 0;
  const dialogProps = { 'data-autotag': 'pdp_embroidery_Modal_Dialog' };

  return (
    <Wrapper>
      <Details>
        <EmbroideryLabel>
          <FormattedMessage
            id="site_pdp.embroidery_label"
            defaultMessage="Embroidery"
          />
          {': '}
          <b>
            {hasEmbroidery ? (
              <FormattedMessage
                id="site_pdp.embroidery_design_added"
                defaultMessage="Design Added"
              />
            ) : (
              <FormattedMessage
                id="site_pdp.embroidery_none_added_label"
                defaultMessage="None Added"
              />
            )}
          </b>
        </EmbroideryLabel>
        {hasEmbroidery ? (
          <>
            <Separator separatorStyle={separatorStyles} color="border" />
            <EmbroideryDetailsWrapper>
              <ItemEmbroideryDetails
                iconPersonalizations={iconPersonalizations}
                textPersonalizations={textPersonalizations}
                personalizations={productEmbroidery}
                separatorCmp={
                  <Separator separatorStyle={separatorStyles} color="border" />
                }
                customizationTextStyles={customizationTextStyles}
                detailLineItemLabelStyles={detailLineItemLabelStyles}
                detailLineItemValueStyles={detailLineItemValueStyles}
              />
            </EmbroideryDetailsWrapper>
          </>
        ) : (
          <>
            <EmbroideryButton
              onClick={handleModalOpen}
              labelStyle={labelStyle}
              variant="secondary"
              data-autotag="pdp_embroidery_cta"
            >
              <ScrubsThread />
              <FormattedMessage
                id="site_pdp.embroidery_cta"
                defaultMessage="Add Custom Embroidery"
              />
            </EmbroideryButton>
            <EmbroideryModal
              dialogProps={dialogProps}
              isOpen={isModalOpen}
              isLoading={isLoading}
              onExit={handleModalClose}
              onCTAClick={onCTAClick}
            />
            <NoReturnsLabel>
              <FormattedMessage
                id="site_pdp.embroidery_no_returns"
                defaultMessage="Embroidered products are final sale. No returns or exchanges possible."
              />
            </NoReturnsLabel>
          </>
        )}
      </Details>
      {hasEmbroidery && (
        <ClearButton variant="unstyled" onClick={handleClear}>
          <FormattedMessage
            id="site_pdp.embroidery_clear"
            defaultMessage="Clear"
          />
        </ClearButton>
      )}
    </Wrapper>
  );
}
